/**
 * Chat upload/message limits (PG-M6).
 *
 * The gateway declares the authoritative limits in `config/media.php`
 * (limits.chat_*) and exposes them to every tenant frontend through the
 * `/tenant/resolve` payload's `limits` block (PG-M5):
 *
 *   chat_photo_max_kb, chat_video_max_kb   — size caps (KB)
 *   chat_photo_mimes,  chat_video_mimes    — comma-separated MIME lists
 *   chat_max_attachments                   — per-message attachment cap
 *   chat_message_max_chars                 — composer maxLength
 *
 * This module converts that contract into the client shape the composer
 * components consume, with per-field fallback to the historical hardcoded
 * defaults so the UI keeps working before the tenant resolves (or when a
 * stale tenant_data payload predates PG-M5). The gateway validators remain
 * the enforcing side; these values only gate the client-side preview.
 *
 * Pure `chatUploadLimitsFromTenant()` is the unit-testable core; the
 * `getChatUploadLimits()` wrapper reads the persisted tenant data.
 */

import { getTenantData } from './tenantContext';

/**
 * Historical defaults — mirrors config/media.php limits.chat_* defaults.
 * Only used when the tenant payload lacks a (valid) field.
 */
export const DEFAULT_CHAT_UPLOAD_LIMITS = Object.freeze({
  photo: Object.freeze({
    maxBytes: 8 * 1024 * 1024, //  8 MB
    mimes: Object.freeze(['image/jpeg', 'image/png', 'image/webp']),
  }),
  video: Object.freeze({
    maxBytes: 25 * 1024 * 1024, // 25 MB
    mimes: Object.freeze(['video/mp4', 'video/webm', 'video/quicktime']),
  }),
  maxAttachments: 5,
  messageMaxChars: 5000,
});

/** @param {*} value @returns {number|null} strictly-positive integer or null */
function positiveInt(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
}

/** @param {*} value @returns {string[]|null} parsed MIME list or null */
function mimeList(value) {
  if (typeof value !== 'string') return null;
  const list = value
    .split(',')
    .map((m) => m.trim().toLowerCase())
    .filter((m) => /^[a-z0-9.+-]+\/[a-z0-9.+-]+$/.test(m));
  return list.length > 0 ? list : null;
}

/**
 * Resolve the effective chat upload/message limits from a persisted
 * tenant payload. Every field independently falls back to the default
 * so a partial or missing limits block can never break the composer.
 *
 * @param {object|null} tenant Persisted tenant payload (with `.limits`).
 * @returns {{
 *   photo: { maxBytes: number, mimes: string[] },
 *   video: { maxBytes: number, mimes: string[] },
 *   maxAttachments: number,
 *   messageMaxChars: number,
 * }}
 */
export function chatUploadLimitsFromTenant(tenant) {
  const limits = tenant?.limits && typeof tenant.limits === 'object' ? tenant.limits : {};

  const photoKb = positiveInt(limits.chat_photo_max_kb);
  const videoKb = positiveInt(limits.chat_video_max_kb);

  return {
    photo: {
      maxBytes: photoKb ? photoKb * 1024 : DEFAULT_CHAT_UPLOAD_LIMITS.photo.maxBytes,
      mimes: mimeList(limits.chat_photo_mimes) || [...DEFAULT_CHAT_UPLOAD_LIMITS.photo.mimes],
    },
    video: {
      maxBytes: videoKb ? videoKb * 1024 : DEFAULT_CHAT_UPLOAD_LIMITS.video.maxBytes,
      mimes: mimeList(limits.chat_video_mimes) || [...DEFAULT_CHAT_UPLOAD_LIMITS.video.mimes],
    },
    maxAttachments:
      positiveInt(limits.chat_max_attachments) ?? DEFAULT_CHAT_UPLOAD_LIMITS.maxAttachments,
    messageMaxChars:
      positiveInt(limits.chat_message_max_chars) ?? DEFAULT_CHAT_UPLOAD_LIMITS.messageMaxChars,
  };
}

/**
 * Effective limits for the active tenant (persisted tenant data), with
 * full fallback when unresolved. Browser-safe; on the server it simply
 * returns the defaults.
 */
export function getChatUploadLimits() {
  return chatUploadLimitsFromTenant(getTenantData());
}
